/**
 * Cliente de Streaming SSE para el VEYRA Creative Director (Etapa 2B.2).
 *
 * Utiliza fetch nativo, ReadableStream y AbortController para recibir
 * respuestas progresivas, cancelaciones y eventos estructurados:
 * - connect
 * - delta
 * - proposal
 * - usage
 * - done
 * - cancelled
 * - error
 */

import { BACKEND_BASE as BASE } from "./backendUrl";

export function formatErrorDetail(rawDetail, status = null) {
  if (!rawDetail) {
    return status ? `Error en la solicitud (HTTP ${status}).` : "Error en el servicio de inferencia.";
  }

  // Si ya es un string limpio
  if (typeof rawDetail === "string") {
    const trimmed = rawDetail.trim();
    return trimmed || (status ? `Error en la solicitud (HTTP ${status}).` : "Error desconocido.");
  }

  // Si es un array de errores de validación estructurados (Pydantic / FastAPI: [{type, loc, msg, input, ctx, url}])
  if (Array.isArray(rawDetail)) {
    const messages = rawDetail
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        // Obtenemos los campos afectados sin filtrar 'input' ni valores privados
        const locParts = Array.isArray(item.loc)
          ? item.loc.filter((p) => p !== "body" && typeof p === "string")
          : [];
        const fieldName = locParts.length > 0 ? locParts.join(".") : null;
        const msgText = typeof item.msg === "string" ? item.msg : null;

        if (fieldName && msgText) {
          if (item.type === "string_too_long") {
            return `El campo '${fieldName}' excede la longitud máxima permitida.`;
          }
          if (item.type === "missing") {
            return `Falta el campo requerido '${fieldName}'.`;
          }
          return `Campo '${fieldName}': ${msgText}`;
        }
        return msgText;
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join("; ");
    }
    return status === 422
      ? "Los datos enviados contienen campos no válidos."
      : `Error de validación (HTTP ${status || 422}).`;
  }

  // Si es un objeto individual { msg, message, detail, error }
  if (typeof rawDetail === "object") {
    if (typeof rawDetail.detail === "string") return rawDetail.detail;
    if (typeof rawDetail.message === "string") return rawDetail.message;
    if (typeof rawDetail.msg === "string") return rawDetail.msg;
    if (typeof rawDetail.error === "string") return rawDetail.error;
    return status ? `Error en la solicitud (HTTP ${status}).` : "Error en el servicio de inferencia.";
  }

  return String(rawDetail);
}

export async function streamAssistantChat({
  payload,
  onConnect,
  onDelta,
  onProposal,
  onUsage,
  onDone,
  onError,
  onCancelled,
  signal,
}) {
  const url = `${BASE}/api/assistant/chat/stream`;

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      credentials: "include",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    if (signal?.aborted || err.name === "AbortError") {
      const abortAction = (typeof signal?.reason === "string" ? signal.reason : signal?.reason?.action) || (signal?.aborted ? "signal_aborted" : err.name);
      console.info("[CreativeDirector] SSE fetch cancelado en cliente:", { action: abortAction });
      onCancelled?.({ status: "cancelled", interrupted: false, action: abortAction });
      return;
    }
    onError?.(err.message || "Error de conexión con el servidor.");
    return;
  }

  if (!response.ok) {
    let rawDetail = null;
    try {
      const errJson = await response.json();
      rawDetail = errJson.detail || errJson.message;
    } catch {
      // Respuesta no-JSON
    }
    const cleanDetail = formatErrorDetail(rawDetail, response.status);
    onError?.(cleanDetail);
    return;
  }

  if (!response.body) {
    onError?.("El navegador no soporta streaming en esta respuesta.");
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let terminalEventReceived = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop() || "";

      for (const block of blocks) {
        if (!block.trim()) continue;
        const lines = block.split("\n");
        let currentEvent = "message";
        let currentData = "";

        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            currentData = line.slice(5).trim();
          }
        }

        if (!currentData) continue;

        try {
          const parsed = JSON.parse(currentData);
          if (currentEvent === "connect") {
            onConnect?.(parsed);
          } else if (currentEvent === "delta") {
            onDelta?.(parsed.content || parsed.text || "");
          } else if (currentEvent === "proposal") {
            onProposal?.(parsed.proposal);
          } else if (currentEvent === "usage") {
            onUsage?.(parsed.data || parsed);
          } else if (currentEvent === "done") {
            terminalEventReceived = true;
            onDone?.(parsed);
          } else if (currentEvent === "cancelled") {
            terminalEventReceived = true;
            console.info("[CreativeDirector] SSE evento cancelled recibido del servidor:", { stage: parsed?.stage });
            onCancelled?.({ ...parsed, status: "cancelled", interrupted: false });
          } else if (currentEvent === "error") {
            terminalEventReceived = true;
            const rawErr = parsed.message || parsed.error || parsed.detail || "Error en el servicio de inferencia.";
            onError?.(formatErrorDetail(rawErr));
          }
        } catch {
          // Ignorar chunks no parseables
        }
      }
    }

    if (!terminalEventReceived) {
      // Si el stream finalizó la lectura sin evento terminal explícito del servidor (desconexión o cierre incompleto)
      console.warn("[CreativeDirector] SSE stream cerrado antes de recibir evento terminal (interrumpido)");
      onCancelled?.({ status: "interrupted", interrupted: true, action: "stream_closed_abruptly" });
    }
  } catch (err) {
    if (signal?.aborted || err.name === "AbortError") {
      const abortAction = (typeof signal?.reason === "string" ? signal.reason : signal?.reason?.action) || (signal?.aborted ? "signal_aborted" : err.name);
      console.info("[CreativeDirector] SSE reader cancelado en cliente:", { action: abortAction });
      onCancelled?.({ status: "cancelled", interrupted: false, action: abortAction });
      return;
    }
    onError?.(err.message || "Error al leer flujo de eventos.");
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // Ignorar si el lock ya fue liberado
    }
  }
}

export default streamAssistantChat;
